import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n";
import PhoneStage, { PARTIAL_SLIDES } from "../../components/phone-stage";
import AppMapScreen from "../newApp/assets/app-map-screen.jpeg";
import AppStoreBadge from "../newApp/assets/app-store-badge.webp";
import GooglePlayBadge from "../newApp/assets/google-play-badge.webp";
import TapkeyLock from "./assets/tapkey-lock.png";
import ControlKit from "./assets/control-kit.png";
import RelayKit from "./assets/relay-kit.png";
import PaytLock from "./assets/payt-lock.jpg";
import JuhuuIcon from "./assets/juhuu-icon.png";

const PARTNER_LOGOS = [
  ["oebb", "ÖBB"],
  ["fellbachgo", "Fellbach.go!"],
  ["koch-partner", "Koch & Partner"],
  ["allgaeutissimo", "Allgäutissimo Bikeshop"],
  ["velodepo", "VELODEPO"],
  ["opg", "OPG"],
  ["greenguard", "Greenguard"],
  ["emz", "emz"],
  ["hochschule-ansbach", "Hochschule Ansbach"],
  ["gablitz", "Gablitz"],
  ["mauerbach", "Mauerbach"],
  ["purkersdorf", "Stadtgemeinde Purkersdorf"],
  ["w5ww", "Wir 5 im Wienerwald"],
];

const HARDWARE_IMAGES = [TapkeyLock, ControlKit, RelayKit, PaytLock];

const PAY_METHODS = [
  "visa",
  "mastercard",
  "maestro",
  "american-express",
  "apple-pay",
  "google-pay",
  "paypal",
  "klarna",
  "discover",
  "diners",
  "jcb",
  "unionpay",
];

const Check = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    className="mt-0.5 h-4 w-4 flex-shrink-0 text-juhuu"
  >
    <path d="m5 12 5 5L20 7" />
  </svg>
);

const kicker = "text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu";
const h2Class =
  "text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink";
const leadClass =
  "text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted";

const KIT_ICONS = [
  <>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </>,
  <>
    <rect x="2" y="7" width="20" height="10" rx="2" />
    <path d="M6 11v2M10 11v2M14 11v2M18 11v2" />
  </>,
  <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />,
  <>
    <rect x="7" y="2" width="10" height="20" rx="2" />
    <path d="M11 18h2" />
  </>,
];

/** Collapsible hardware kit row — first item starts expanded. */
function KitItem({
  index,
  defaultOpen,
  image,
  name,
  tag,
  title,
  description,
  specs,
}: {
  index: number;
  defaultOpen?: boolean;
  image: string;
  name: string;
  tag: string;
  title: string;
  description: string;
  specs: string[];
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border-b border-line last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`flex w-full items-center gap-3 px-5 py-4 text-left transition-colors ${
          open ? "bg-juhuu-soft" : "hover:bg-bg-alt"
        }`}
      >
        <span
          className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl ${
            open ? "bg-juhuu text-white" : "bg-juhuu-soft text-juhuu"
          }`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="h-5 w-5"
          >
            {KIT_ICONS[index]}
          </svg>
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[1.02rem] font-extrabold text-ink">{name}</span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[.62rem] font-extrabold uppercase tracking-[.08em] text-juhuu-deep">
              {tag}
            </span>
          </span>
          <span className="mt-0.5 block text-[.86rem] text-muted">{title}</span>
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          className={`h-[18px] w-[18px] flex-shrink-0 text-muted transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="grid gap-5 px-5 pb-6 pt-4 sm:grid-cols-[150px_1fr]">
          <div className="grid place-items-center rounded-xl border border-line bg-white p-3">
            <img
              src={image}
              alt={name}
              loading="lazy"
              className="max-h-28 w-auto object-contain"
            />
          </div>
          <div>
            <p className="text-[.92rem] leading-[1.62] text-muted">
              {description}
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {specs.map((s, i) => (
                <span
                  key={i}
                  className="rounded-full border border-line bg-bg-alt px-2.5 py-1 text-[.68rem] font-bold text-ink-2"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
      >
        <span className="text-[1.05rem] font-bold text-ink">{q}</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`h-5 w-5 flex-shrink-0 text-juhuu transition-transform ${
            open ? "rotate-45" : ""
          }`}
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
      {open && (
        <p className="pb-5 pr-8 text-[.95rem] leading-[1.7] text-muted">{a}</p>
      )}
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();

  const list = (base: string, n: number) =>
    Array.from({ length: n }, (_, i) => t(`${base}.${i}`));

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <main>
        {/* ── Hero ─────────────────────────────────────────── */}
        <section className="relative overflow-hidden px-4 md:px-8 pt-32 pb-20">
          <div className="pointer-events-none absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-juhuu-glow blur-[90px]" />
          <div className="pointer-events-none absolute -bottom-32 -right-20 h-[380px] w-[380px] rounded-full bg-[rgba(0,180,255,0.10)] blur-[90px]" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 md:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-juhuu-soft-2 bg-juhuu-soft px-3.5 py-1.5 text-sm font-bold text-juhuu-deep">
                <span className="h-[7px] w-[7px] rounded-full bg-juhuu" />
                {t("routes.home.page.heroBadge")}
              </div>

              <h1 className="mt-5 text-[clamp(2.3rem,4.4vw,3.9rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-ink">
                {t("routes.home.page.heroTitle")}{" "}
                <span className="text-juhuu">
                  {t("routes.home.page.heroHighlight")}
                </span>
                .
              </h1>

              <p className={`mt-5 max-w-[540px] ${leadClass}`}>
                {t("routes.home.page.heroDescription")}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/meeting"
                  className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
                >
                  {t("routes.home.page.heroCtaDemo")}
                </Link>
                <a
                  href="#products"
                  className="inline-flex items-center rounded-full border border-line-strong px-7 py-3.5 font-semibold text-ink transition-colors hover:border-juhuu hover:text-juhuu"
                >
                  {t("routes.home.page.heroCtaProducts")}
                </a>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <a
                  href="https://apps.apple.com/at/app/juhuu/id1603108125"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex transition-transform hover:-translate-y-0.5"
                >
                  <img
                    src={AppStoreBadge}
                    alt={t("routes.app.appStoreAlt")}
                    className="block h-[46px] w-auto"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.juhuu_bikebox.juhuu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex transition-transform hover:-translate-y-0.5"
                >
                  <img
                    src={GooglePlayBadge}
                    alt={t("routes.app.googlePlayAlt")}
                    className="block h-[46px] w-auto"
                  />
                </a>
              </div>

              <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3">
                {list("routes.home.page.heroChecks", 3).map((c, i) => (
                  <span
                    key={i}
                    className="flex items-center gap-2 text-[.92rem] font-bold text-ink-2"
                  >
                    <Check />
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative flex justify-center">
              <div className="pointer-events-none absolute top-8 left-6 h-80 w-80 rounded-full bg-juhuu-glow blur-[90px]" />
              <div className="relative z-10 w-[300px] max-w-full animate-floaty rounded-[42px] bg-ink p-2.5 shadow-2xl">
                <div className="overflow-hidden rounded-[33px] bg-white">
                  <img
                    src={AppMapScreen}
                    alt={t("routes.app.heroPhoneAlt")}
                    className="block h-auto w-full"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Trust marquee — inside the hero, above a top border */}
          <div className="relative mx-auto mt-[clamp(3rem,6vw,5rem)] max-w-7xl border-t border-line pt-8">
            <p className="mb-6 text-center text-[.82rem] font-bold uppercase tracking-[.14em] text-muted">
              {t("routes.home.page.trustedBy")}
            </p>
            <div className="w-full overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
              <div className="flex w-max animate-marquee items-center gap-[clamp(2rem,5vw,4rem)] hover:[animation-play-state:paused]">
                {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map(([slug, name], i) => (
                  <span key={i} className="inline-flex flex-shrink-0">
                    <img
                      src={require(`../newApp/assets/logos/${slug}.png`)}
                      alt={name}
                      loading="lazy"
                      className="h-10 w-auto max-w-[170px] object-contain opacity-55 transition-opacity hover:opacity-100"
                    />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── One toolkit (cities + manufacturers) ─────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 grid items-end gap-10 lg:grid-cols-[1.2fr_1fr]">
              <div>
                <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-muted">
                  {t("routes.home.page.offerKicker")}
                </p>
                <h2 className={`mt-4 ${h2Class}`}>
                  {t("routes.home.page.offerTitleLine1")}
                  <br />
                  {t("routes.home.page.offerTitleLine2")}
                </h2>
              </div>
              <p className="text-[1.02rem] leading-[1.7] text-muted">
                {t("routes.home.page.offerDescription")}
              </p>
            </div>

            <div className="grid items-stretch gap-5 lg:grid-cols-2">
              {/* Cities */}
              <div className="flex flex-col rounded-2xl border border-line bg-white p-8 shadow-sm">
                <span className="self-start rounded-full border border-juhuu-soft-2 bg-juhuu-soft px-2.5 py-1.5 text-[.66rem] font-extrabold uppercase tracking-[.09em] text-juhuu-deep">
                  {t("routes.home.page.citiesKicker")}
                </span>
                <div className="my-[1.1rem] grid h-12 w-12 place-items-center rounded-[13px] bg-juhuu text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)]">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    className="h-6 w-6"
                  >
                    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
                    <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
                  </svg>
                </div>
                <h3 className="text-[1.32rem] font-extrabold tracking-[-0.02em] text-ink">
                  {t("routes.home.page.citiesTitle")}
                </h3>
                <p className="mt-2.5 text-[.95rem] leading-[1.62] text-muted">
                  {t("routes.home.page.citiesDescription")}
                </p>
                <div className="mt-6 flex flex-col gap-2.5">
                  {list("routes.home.page.citiesBullets", 4).map((b, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2.5 text-[.92rem] font-medium text-ink-2"
                    >
                      <Check />
                      {b}
                    </div>
                  ))}
                </div>
              </div>

              {/* Manufacturers */}
              <div className="flex flex-col rounded-2xl border border-line bg-white p-8 shadow-sm">
                <span className="self-start rounded-full border border-juhuu-soft-2 bg-juhuu-soft px-2.5 py-1.5 text-[.66rem] font-extrabold uppercase tracking-[.09em] text-juhuu-deep">
                  {t("routes.home.page.manufacturersKicker")}
                </span>
                <div className="my-[1.1rem] grid h-12 w-12 place-items-center rounded-[13px] bg-juhuu text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)]">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    className="h-6 w-6"
                  >
                    <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
                    <path d="M17 18h.01M12 18h.01M7 18h.01" />
                  </svg>
                </div>
                <h3 className="text-[1.32rem] font-extrabold tracking-[-0.02em] text-ink">
                  {t("routes.home.page.manufacturersTitle")}
                </h3>
                <p className="mt-2.5 text-[.95rem] leading-[1.62] text-muted">
                  {t("routes.home.page.manufacturersDescription")}
                </p>
                <div className="mt-5 flex flex-col gap-2.5">
                  {[
                    {
                      letter: "A",
                      title: t("routes.home.page.manufacturersOptionATitle"),
                      desc: t("routes.home.page.manufacturersOptionADescription"),
                    },
                    {
                      letter: "B",
                      title: t("routes.home.page.manufacturersOptionBTitle"),
                      desc: t("routes.home.page.manufacturersOptionBDescription"),
                    },
                  ].map((o) => (
                    <div
                      key={o.letter}
                      className="flex gap-3.5 rounded-xl border border-line bg-bg-alt p-4"
                    >
                      <span className="grid h-[30px] w-[30px] flex-shrink-0 place-items-center rounded-[9px] bg-juhuu text-[.88rem] font-extrabold text-white">
                        {o.letter}
                      </span>
                      <div>
                        <div className="text-[.94rem] font-extrabold text-ink">
                          {o.title}
                        </div>
                        <p className="mt-1 text-[.84rem] leading-[1.52] text-muted">
                          {o.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Products intro ───────────────────────────────── */}
        <section id="products" className="scroll-mt-20 px-4 md:px-8 pt-[clamp(4rem,8vw,7rem)]">
          <div className="mx-auto max-w-7xl text-center">
            <p className={kicker}>{t("routes.home.page.productsKicker")}</p>
            <h2 className={`mt-4 ${h2Class}`}>
              {t("routes.home.page.productsTitle")}
            </h2>
            <p className={`mx-auto mt-5 max-w-[560px] ${leadClass}`}>
              {t("routes.home.page.productsLead")}
            </p>
          </div>
        </section>

        {/* ── 01 JUHUU App ─────────────────────────────────── */}
        <section className="px-4 md:px-8 py-16">
          <div className="mx-auto grid max-w-7xl items-center gap-12 md:grid-cols-2">
            <div>
              <p className={kicker}>01 — {t("routes.home.page.appKicker")}</p>
              <h2 className="mt-3 text-[clamp(1.6rem,2.5vw,2.4rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.home.page.appTitleLine1")}
                <br />
                {t("routes.home.page.appTitleLine2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.home.page.appDescription")}
              </p>
              <div className="flex flex-col gap-2.5">
                {list("routes.home.page.appBullets", 4).map((b, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 text-[.95rem] font-medium text-ink-2"
                  >
                    <Check />
                    {b}
                  </div>
                ))}
              </div>
              <Link
                to="/app"
                className="mt-7 inline-flex items-center gap-1.5 rounded-full bg-juhuu-soft px-6 py-3 font-semibold text-juhuu-deep transition-colors hover:bg-juhuu-soft-2"
              >
                {t("routes.home.page.appCta")}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-4 w-4"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </Link>
            </div>

            <PhoneStage slides={PARTIAL_SLIDES} />
          </div>

          {/* deployment trio */}
          <div className="mx-auto mt-[clamp(2.4rem,4vw,3.4rem)] max-w-7xl overflow-hidden rounded-2xl border border-line bg-line">
            <div className="grid gap-px md:grid-cols-3">
              {[0, 1, 2].map((i) => {
                return (
                  <div key={i} className="bg-white p-8">
                    <div className="text-[.68rem] font-bold uppercase tracking-[.1em] text-juhuu">
                      {t(`routes.home.page.appDeployCells.${i}.tag`)}
                    </div>
                    <div className="my-4 grid h-[38px] w-[38px] place-items-center rounded-xl border border-juhuu bg-juhuu text-white">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        className="h-5 w-5"
                      >
                        {i === 0 && (
                          <>
                            <circle cx="12" cy="12" r="9" />
                            <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
                          </>
                        )}
                        {i === 1 && (
                          <>
                            <rect x="7" y="2" width="10" height="20" rx="2" />
                            <path d="M11 18h2" />
                          </>
                        )}
                        {i === 2 && (
                          <>
                            <rect x="5" y="2" width="14" height="20" rx="3" />
                            <circle cx="12" cy="18" r="1" />
                            <path d="M9 6h6" />
                          </>
                        )}
                      </svg>
                    </div>
                    <h3 className="text-[1.15rem] font-bold text-ink">
                      {t(`routes.home.page.appDeployCells.${i}.title`)}
                    </h3>
                    <p className="mt-2 text-[.9rem] leading-[1.6] text-muted">
                      {t(`routes.home.page.appDeployCells.${i}.description`)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* payments + identity strip */}
          <div className="mx-auto mt-3.5 max-w-7xl overflow-hidden rounded-2xl border border-line bg-line">
            <div className="grid gap-px lg:grid-cols-[1.05fr_.95fr]">
              <div className="bg-white p-7">
                <p className="mb-4 text-[.78rem] font-bold uppercase tracking-[.16em] text-muted">
                  {t("routes.home.page.payStripKicker")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {PAY_METHODS.map((p) => (
                    <img
                      key={p}
                      src={require(`../newApp/assets/pay/${p}.svg`)}
                      alt={p}
                      loading="lazy"
                      className="block h-[34px] w-auto rounded-lg bg-white shadow-sm"
                    />
                  ))}
                </div>
              </div>
              <div className="bg-white p-7">
                <p className="mb-4 text-[.78rem] font-bold uppercase tracking-[.16em] text-muted">
                  {t("routes.home.page.includedKicker")}
                </p>
                <div className="flex flex-col gap-2.5">
                  {list("routes.home.page.includedItems", 3).map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2.5 text-[.92rem] font-semibold text-ink-2"
                    >
                      <Check />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 02 Dashboard ─────────────────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-16">
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div className="order-2 overflow-hidden rounded-2xl border border-line bg-white shadow-xl lg:order-1">
              <div className="flex items-center gap-3 border-b border-line bg-bg-alt px-[1.1rem] py-[.7rem]">
                <div className="flex gap-1.5">
                  <span className="h-[11px] w-[11px] rounded-full bg-line-strong" />
                  <span className="h-[11px] w-[11px] rounded-full bg-line-strong" />
                  <span className="h-[11px] w-[11px] rounded-full bg-line-strong" />
                </div>
                <div className="flex h-7 flex-1 items-center rounded-full border border-line bg-white px-3.5 text-[.72rem] text-light">
                  dashboard.juhuu.app
                </div>
              </div>
              <div className="grid grid-cols-[148px_1fr]">
                {/* Sidebar */}
                <div className="border-r border-line bg-bg-alt p-3">
                  <div className="mb-3 flex items-center gap-2 border-b border-line px-1 pb-3">
                    <span className="grid h-[26px] w-[26px] place-items-center overflow-hidden rounded-[7px] bg-ink">
                      <img
                        src={JuhuuIcon}
                        alt="JUHUU"
                        className="h-[17px] w-[17px] object-contain"
                      />
                    </span>
                    <span className="text-[.8rem] font-extrabold text-ink">
                      JUHUU
                    </span>
                  </div>
                  <span className="flex items-center gap-2 rounded-[9px] bg-juhuu-soft px-2 py-1.5 text-[.78rem] font-bold text-juhuu-deep">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="h-[15px] w-[15px] flex-shrink-0"
                    >
                      <path d="M3 11l9-8 9 8M5 9v11h14V9" />
                    </svg>
                    {t("routes.home.page.dashboardNavHome")}
                  </span>
                  <div className="mx-1 mb-1 mt-3 text-[.6rem] font-bold uppercase tracking-[.1em] text-light">
                    {t("routes.home.page.dashboardNavOperators")}
                  </div>
                  {[
                    { k: "dashboardNavBooking", icon: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></> },
                    { k: "dashboardNavDevices", icon: <><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /></> },
                    { k: "dashboardNavBilling", icon: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></> },
                    { k: "dashboardNavSupport", icon: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></> },
                  ].map((n) => (
                    <span
                      key={n.k}
                      className="flex items-center gap-2 rounded-[9px] px-2 py-1.5 text-[.78rem] font-semibold text-muted"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="h-[15px] w-[15px] flex-shrink-0"
                      >
                        {n.icon}
                      </svg>
                      {t(`routes.home.page.${n.k}`)}
                    </span>
                  ))}
                </div>

                {/* Main panel */}
                <div className="min-w-0 bg-white px-5 py-4">
                  <div className="text-[1.15rem] font-extrabold text-ink">
                    Dashboard
                  </div>
                  <p className="mb-3.5 text-[.74rem] text-muted">
                    {t("routes.home.page.dashboardTitleLine2")}
                  </p>
                  <div className="mb-3.5 grid grid-cols-2 gap-2">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-line bg-white px-3 py-2.5"
                      >
                        <div className="text-[.6rem] font-semibold uppercase leading-tight tracking-[.06em] text-light">
                          {t(`routes.home.page.dashboardKpis.${i}.label`)}
                        </div>
                        <div className="mt-1.5 text-[1.15rem] font-extrabold leading-none tracking-[-0.02em] text-ink tabular-nums">
                          {t(`routes.home.page.dashboardKpis.${i}.value`)}
                        </div>
                        <div
                          className={`mt-1 text-[.64rem] font-bold tabular-nums ${
                            i < 2 ? "text-juhuu" : "text-light"
                          }`}
                        >
                          {t(`routes.home.page.dashboardKpis.${i}.delta`)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="overflow-hidden rounded-xl border border-line shadow-sm">
                    <div className="flex items-center gap-1.5 border-b border-line px-3.5 py-2.5 text-[.78rem] font-bold text-ink">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="h-[15px] w-[15px] text-juhuu"
                      >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v4M12 16h.01" />
                      </svg>
                      {t("routes.home.page.dashboardIncidents")}
                    </div>
                    {[3, 2, 1].map((n) => (
                      <div
                        key={n}
                        className="flex items-center justify-between gap-2 border-b border-line px-3.5 py-2.5 text-[.74rem] last:border-b-0"
                      >
                        <span className="truncate text-ink-2">
                          Fahrradbox {n} · E-MTB
                        </span>
                        <span className="flex-shrink-0 whitespace-nowrap rounded-full bg-[#fdf3d4] px-2 py-0.5 text-[.6rem] font-bold text-[#8a6d12]">
                          {t("routes.home.page.dashboardIncidentAwaiting")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right column — copy */}
            <div className="order-1 lg:order-2">
              <p className={kicker}>
                02 — {t("routes.home.page.dashboardKicker")}
              </p>
              <h2 className="mt-3 text-[clamp(1.6rem,2.5vw,2.4rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.home.page.dashboardTitleAlt1")}
                <br />
                {t("routes.home.page.dashboardTitleAlt2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.home.page.dashboardLead")}
              </p>
              <div className="flex flex-col gap-2.5">
                {list("routes.home.page.dashboardBullets", 4).map((b, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 text-[.95rem] font-medium text-ink-2"
                  >
                    <Check />
                    {b}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {list("routes.home.page.dashboardChips", 7).map((c, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-line bg-white px-3 py-1.5 text-[.82rem] font-semibold text-ink-2"
                  >
                    {c}
                  </span>
                ))}
              </div>
              <Link
                to="/dashboard"
                className="mt-7 inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-white px-6 py-3 font-semibold text-ink transition-colors hover:border-juhuu hover:text-juhuu"
              >
                {t("routes.home.page.dashboardCta")}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-4 w-4"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* ── 03 Locking systems ───────────────────────────── */}
        <section className="px-4 md:px-8 py-16">
          <div className="mx-auto max-w-7xl">
            <div className="grid items-start gap-12 md:grid-cols-2">
              <div>
                <div className="mb-1 flex items-center gap-3">
                  <p className={kicker}>
                    03 — {t("routes.home.page.lockingKicker")}
                  </p>
                  <span className="inline-block rounded-full bg-juhuu px-2.5 py-1 text-[.7rem] font-bold uppercase tracking-[.08em] text-white">
                    {t("routes.home.page.lockingTagPill")}
                  </span>
                </div>
                <h2 className="mt-3 text-[clamp(1.6rem,2.5vw,2.4rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                  {t("routes.home.page.lockingTitleLine1")}
                  <br />
                  {t("routes.home.page.lockingTitleLine2")}
                </h2>
                <p className="my-5 text-base leading-[1.65] text-muted">
                  {t("routes.home.page.lockingDescription")}
                </p>
                <div className="flex flex-col gap-2.5">
                  {list("routes.home.page.lockingBullets", 4).map((b, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2.5 text-[.95rem] font-medium text-ink-2"
                    >
                      <Check />
                      {b}
                    </div>
                  ))}
                </div>
                <Link
                  to="/locking-systems"
                  className="mt-7 inline-flex items-center gap-1.5 rounded-full bg-juhuu-soft px-6 py-3 font-semibold text-juhuu-deep transition-colors hover:bg-juhuu-soft-2"
                >
                  {t("routes.home.page.lockingCta")}
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-4 w-4"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </Link>
              </div>

              <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-sm">
                {[0, 1, 2, 3].map((i) => (
                  <KitItem
                    key={i}
                    index={i}
                    defaultOpen={i === 0}
                    image={HARDWARE_IMAGES[i]}
                    name={t(`routes.home.page.hardwareItems.${i}.name`)}
                    tag={t(`routes.home.page.hardwareItems.${i}.tag`)}
                    title={t(`routes.home.page.hardwareItems.${i}.title`)}
                    description={t(
                      `routes.home.page.hardwareItems.${i}.description`
                    )}
                    specs={list(`routes.home.page.hardwareSpecs.${i}`, i === 0 ? 4 : 3)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Plug & play (4 steps) ────────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 max-w-[640px]">
              <p className={kicker}>{t("routes.home.page.stepsKicker")}</p>
              <h2 className={`mt-4 ${h2Class}`}>
                {t("routes.home.page.stepsTitleLine1")}
                <br />
                {t("routes.home.page.stepsTitleLine2")}
              </h2>
              <p className={`mt-5 max-w-[570px] ${leadClass}`}>
                {t("routes.home.page.stepsDescription")}
              </p>
            </div>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="mb-4 grid h-[46px] w-[46px] place-items-center rounded-[13px] border border-juhuu-soft-2 bg-juhuu-soft text-[1.05rem] font-extrabold text-juhuu">
                    {i + 1}
                  </div>
                  <h3 className="text-[1.2rem] font-bold tracking-[-0.02em] text-ink">
                    {t(`routes.home.page.steps.${i}.title`)}
                  </h3>
                  <p className="mt-2 text-[.92rem] leading-[1.6] text-muted">
                    {t(`routes.home.page.steps.${i}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Industries ───────────────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-[560px]">
              <p className={kicker}>{t("routes.home.page.industriesKicker")}</p>
              <h2 className={`mt-4 ${h2Class}`}>
                {t("routes.home.page.industriesTitle")}
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {[
                {
                  href: "/industries#parking",
                  icon: (
                    <>
                      <rect x="3" y="7" width="18" height="13" rx="2" />
                      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </>
                  ),
                },
                {
                  href: "/industries#rental",
                  icon: (
                    <>
                      <circle cx="6" cy="17" r="3" />
                      <circle cx="18" cy="17" r="3" />
                      <path d="M6 17 10 9h4m1 8-4-8m0 0 2-3h3" />
                    </>
                  ),
                },
                {
                  href: "/industries#custom",
                  icon: <path d="M12 5v14M5 12h14" />,
                },
              ].map((u, i) => (
                <Link
                  key={i}
                  to={u.href}
                  className="rounded-2xl border border-line bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="mb-4 grid h-[46px] w-[46px] place-items-center rounded-[13px] border border-juhuu-soft-2 bg-juhuu-soft text-juhuu">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="h-[22px] w-[22px]"
                    >
                      {u.icon}
                    </svg>
                  </div>
                  <h3 className="text-[1.2rem] font-bold tracking-[-0.02em] text-ink">
                    {t(`routes.home.page.industries.${i}.title`)}
                  </h3>
                  <p className="mt-2 text-[.88rem] leading-[1.6] text-muted">
                    {t(`routes.home.page.industries.${i}.description`)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── Pricing (dark) ───────────────────────────────── */}
        <section className="relative overflow-hidden bg-ink px-4 md:px-8 py-20">
          <div className="pointer-events-none absolute -top-24 right-0 h-[420px] w-[420px] rounded-full bg-juhuu/25 blur-[100px]" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu-soft-2">
                {t("routes.home.page.pricingKicker")}
              </p>
              <h2 className="mt-4 text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-white">
                {t("routes.home.page.pricingTitleLine1")}
                <br />
                {t("routes.home.page.pricingTitleLine2")}
              </h2>
              <p className="mt-4 text-[1.05rem] leading-[1.65] text-white/70">
                {t("routes.home.page.pricingDescription")}
              </p>
              <div className="mt-7 flex flex-col gap-3">
                {list("routes.home.page.pricingPoints", 3).map((p, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 text-[.95rem] font-medium text-white/85"
                  >
                    <span className="text-juhuu-soft-2">→</span>
                    {p}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/15 bg-white/5 p-8 backdrop-blur">
              <p className="text-[.78rem] font-bold uppercase tracking-[.1em] text-juhuu-soft-2">
                {t("routes.home.page.pricingCardLabel")}
              </p>
              <div className="mt-4 flex flex-wrap items-baseline gap-2">
                <span className="text-[2.6rem] font-extrabold leading-none text-white">
                  {t("routes.home.page.pricingFrom")}
                </span>
                <span className="text-[.95rem] font-medium text-white/60">
                  {t("routes.home.page.pricingPeriod")}
                </span>
              </div>
              <p className="mt-3 text-[.9rem] leading-[1.6] text-white/70">
                {t("routes.home.page.pricingNote")}
              </p>

              <div className="mt-6 flex flex-col gap-2.5 border-t border-white/15 pt-6">
                {list("routes.home.page.pricingIncludes", 4).map((inc, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 text-[.92rem] text-white/85"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      className="mt-0.5 h-4 w-4 flex-shrink-0 text-juhuu-soft-2"
                    >
                      <path d="m5 12 5 5L20 7" />
                    </svg>
                    {inc}
                  </div>
                ))}
              </div>

              <Link
                to="/meeting"
                className="mt-7 inline-flex w-full items-center justify-center rounded-full bg-juhuu px-6 py-3.5 font-semibold text-white transition-colors hover:bg-juhuu-ink"
              >
                {t("routes.home.page.pricingCta")}
              </Link>

              <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[.78rem] font-medium text-white/55">
                {list("routes.home.page.pricingBadges", 3).map((b, i) => (
                  <span key={i}>{b}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto max-w-3xl">
            <p className={kicker}>{t("routes.home.page.faqKicker")}</p>
            <h2 className={`mt-4 mb-8 ${h2Class}`}>
              {t("routes.home.page.faqTitle")}
            </h2>
            <div>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <FaqItem
                  key={i}
                  q={t(`routes.home.page.faqs.${i}.q`)}
                  a={t(`routes.home.page.faqs.${i}.a`)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────── */}
        <section className="border-t border-line px-4 md:px-8 py-20 text-center">
          <div className="mx-auto max-w-[640px]">
            <h2 className={h2Class}>{t("routes.home.page.finalCtaTitle")}</h2>
            <p className={`mx-auto my-6 max-w-[480px] ${leadClass}`}>
              {t("routes.home.page.finalCtaDescription")}
            </p>
            <Link
              to="/meeting"
              className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
            >
              {t("routes.home.page.finalCtaButton")}
            </Link>

            <div className="mt-12 flex flex-wrap justify-center gap-x-12 gap-y-4 border-t border-line pt-8">
              {[0, 1].map((i) => (
                <span key={i}>
                  <strong className="font-extrabold text-ink">
                    {t(`routes.home.page.finalStats.${i}.value`)}
                    {t(`routes.home.page.finalStats.${i}.sub`)
                      ? ` ${t(`routes.home.page.finalStats.${i}.label`)}`
                      : ""}
                  </strong>{" "}
                  <span className="text-[.9rem] text-muted">
                    {t(`routes.home.page.finalStats.${i}.sub`) ||
                      t(`routes.home.page.finalStats.${i}.label`)}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
